import React from 'react';
import theme from './MainRouting.module.scss';
import { Redirect, Route, Switch } from 'react-router-dom';
import LoginPage from '@/components/LoginPage';
import { Home } from '@/components/Home';
import UsersTable from '@/components/Users/UsersTable';
import { useAsyncState, useLocalStorage } from '@/utils/hooks';
import { getCurrentUser, hasAccessLevel } from '@/services/auth';
import Sidebar from '@/components/Sidebar/Sidebar';
import LogoutPage from '@/components/LogoutPage';
import ProjectsTable from '@/components/Projects/ProjectsTable';
import ProjectDetails from '@/components/Projects/ProjectDetails';
import ProfileTable from '@/components/Profiles/ProfilesTable';
import ProfileDetails from '@/components/Profiles/Profile/ProfileDetails';
import BygrStatisticsDashboard from '@/components/Statistics/BygrStatisticsDashboard';
import OverTimeStatisticsPage from '@/components/OverTimeStatistics/OverTimeStatisticsPage';
import ResidentialUnit from '@/components/Projects/ResidentialUnit';
import { About } from '@/components/About/About';
import { LicenseOverview } from '@/components/Licenses/LicenseOverview';
import { UserDetails } from '@/components/Users/UserDetails';
import InvitationsPage from '@/components/Invitations/InvitationsPage';
import { FeaturesOverview } from '@/components/Features/FeaturesOverview';
import { FeatureInfo } from '@/components/Features/FeatureInfo';
import ElectricitySupplierTable from '@/components/ServiceProviders/ElectricitySupplierTable';
import ElectricitySupplierDetails from '@/components/ServiceProviders/ElectricitySupplierDetails';
import SettingsPage from '@/components/Settings/SettingsPage';
import CommunicorePage from '@/components/Communicore/CommunicorePage';
import SystemHealthPage from '@/components/SystemHealth/SystemHealthPage';
import EmailTemplatesPage from '@/components/EmailTemplates/EmailTemplatesPage';
import EmailContentDetails from '@/components/EmailTemplates/EmailContentDetails';

interface MainRoutingProps {}

const MainRouting: React.FC<MainRoutingProps> = () => {
    const [user] = useAsyncState(() => getCurrentUser(), [], null);
    const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('sidebarCollapsed', false);

    if (!user) {
        return null;
    }

    if (!user.isLoggedIn) {
        return <LoginPage />;
    }

    return (
        <div className={theme.content}>
            <Sidebar accessLevel={user.accessLevel} collapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />
            <Switch>
                <Route exact path="/" component={Home} />
                <Route exact path="/about" component={About} />
                <Route exact path="/statistics" component={BygrStatisticsDashboard} />
                <Route exact path="/over-time-statistics" component={OverTimeStatisticsPage} />
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Route exact path="/system-health/:tab" component={SystemHealthPage} />
                )}
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Redirect exact path="/system-health" to="/system-health/health" />
                )}

                {/* <Route exact path="/login" component={Home} /> */}
                <Route exact path="/logout" component={LogoutPage} />
                <Route exact path="/users" component={UsersTable} />
                <Route exact path="/licenses" component={LicenseOverview} />

                <Route exact path="/users/:userId/:tab" component={UserDetails} />
                <Redirect exact path="/users/:userId" to="/users/:userId/dashboard" />

                <Route exact path="/projects" component={ProjectsTable} />
                <Route exact path="/projects/:projectId/units/:residentialUnitId" component={ResidentialUnit} />
                <Route exact path="/projects/:projectId/:tab" component={ProjectDetails} />
                <Redirect exact path="/projects/:projectId" to="/projects/:projectId/dashboard" />

                <Route exact path="/profiles" component={ProfileTable} />
                <Route exact path="/profiles/:profileId/:tab" component={ProfileDetails} />
                <Redirect exact path="/profiles/:profileId" to="/profiles/:profileId/dashboard" />

                <Route exact path="/invitations/:tab" component={InvitationsPage} />
                <Redirect exact path="/invitations" to="/invitations/profile" />

                <Route exact path={'/features'} component={FeaturesOverview} />
                <Route exact path={'/features/:feature'} component={FeatureInfo} />

                <Route exact path="/electricity-suppliers" component={ElectricitySupplierTable} />
                <Route exact path="/electricity-suppliers/:offerId" component={ElectricitySupplierDetails} />

                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Route exact path="/communicore/:tab" component={CommunicorePage} />
                )}
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Redirect exact path="/communicore" to="/communicore/outgoing-emails" />
                )}

                {/* Detail route first — /email-templates/:tab would otherwise not match it anyway, but keep intent explicit. */}
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Route exact path="/email-templates/contents/:name" component={EmailContentDetails} />
                )}
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Route exact path="/email-templates/:tab" component={EmailTemplatesPage} />
                )}
                {hasAccessLevel(user.accessLevel, 'Advanced') && (
                    <Route
                        exact
                        path="/email-templates"
                        // Not a plain Redirect: the query string carries the scope and must survive.
                        render={({ location }) => (
                            <Redirect to={{ ...location, pathname: '/email-templates/contents' }} />
                        )}
                    />
                )}

                {user.accessLevel === 'Admin' && (
                    <Route exact path="/settings/:tab" component={SettingsPage} />
                )}
                {user.accessLevel === 'Admin' && <Redirect exact path="/settings" to="/settings/users" />}

            </Switch>
        </div>
    );
};

export default MainRouting;
